// UserList.js
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, OverlayTrigger, Tooltip, Form, InputGroup, FormControl,
  Pagination, Spinner, Badge, Alert
} from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';
import UserForm from './UserForm';
import {
  InfoCircle, Trash, Wrench, Search, ArrowDownUp, PlusCircle, PlusCircleFill
} from "react-bootstrap-icons";
import { useAuth } from '../contexts/AuthContext';

function UserList() {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [upfitters, setUpfitters] = useState([]);

  const [filteredUsers, setFilteredUsers] = useState([]);

  // NEW: pending account requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestToCreate, setRequestToCreate] = useState(null); // pass into UserForm

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToShow, setUserToShow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const { getData, deleteData } = useFirestore();
  const [isHovered, setIsHovered] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showRequestDeleteModal, setShowRequestDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [deletingRequest, setDeletingRequest] = useState(false);

  const getUserRole = () => {
    try {
      const customAttributes = currentUser?.reloadUserInfo?.customAttributes;
      if (customAttributes) {
        const parsedAttributes = JSON.parse(customAttributes);
        return parsedAttributes.role || null;
      }
    } catch (error) {
      console.error('Error parsing customAttributes:', error);
    }
    return null;
  };

  // helper: format Firestore Timestamp or Date
  const fmt = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  };

  const refreshUsersAndRequests = async () => {
    const userData = await getData('users');
    const customerData = await getData('customers');
    const upfitterData = await getData('Upfitters');

    // NEW: fetch accountRequests (pending first)
    const requests = await getData('accountRequests'); // collection name from previous step
    const pending = (requests || []).filter(r => (r.status || 'pending') === 'pending');

    const userRole = getUserRole();
    if (userRole === 'BDC') {
      const filtered = userData.filter(u => u.role === 'Customer');
      setUsers(filtered);
      setFilteredUsers(filtered);
    } else {
      setUsers(userData);
      setFilteredUsers(userData);
    }
    setCustomers(customerData);
    setUpfitters(upfitterData);
    setPendingRequests(pending);

    setTotalRows((userRole === 'BDC' ? userData.filter(u => u.role === 'Customer') : userData).length);
  };

  useEffect(() => {
    refreshUsersAndRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // upfitter/customer helpers (unchanged)
  const getUpfitterName = (upfNbr) => {
    if (!upfNbr || upfNbr === 'None') return 'None';
    const upfitter = upfitters.find(u => u.strUpfitterNbr === upfNbr);
    return upfitter ? upfitter.strUpfitterName : 'Unknown Upfitter';
  };

  const getCustomerName = (custNbr) => {
    if (!custNbr || custNbr === 'None') return 'None';
    const customer = customers.find(c => c.strCustNbr === custNbr);
    return customer ? customer.strCustName : 'Unknown Customer';
  };

  const getCustomerNames = (custNbrs) => {
    if (!custNbrs || custNbrs.length === 0) return 'None';
    const arr = Array.isArray(custNbrs) ? custNbrs : [custNbrs];
    const names = arr.map(cn => {
      const c = customers.find(x => x.strCustNbr === cn);
      return c ? c.strCustName : 'Unknown Customer';
    });
    return names.join(', ');
  };

  useEffect(() => {
    const lower = searchQuery.toLowerCase();
    const filtered = users.filter(user => (
      user.name.toLowerCase().includes(lower) ||
      user.email.toLowerCase().includes(lower) ||
      getCustomerName(user.customer).toLowerCase().includes(lower) ||
      user.upfitter.toLowerCase().includes(lower) ||
      user.role.toLowerCase().includes(lower)
    ));
    setFilteredUsers(filtered);
    setTotalRows(filtered.length);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchQuery, customers]);

  // NEW: delete a pending account request
  const handleDeleteRequest = async () => {
    if (!requestToDelete?.id) return;
    setDeletingRequest(true);
    try {
      await deleteData('accountRequests', requestToDelete.id);
      setShowRequestDeleteModal(false);
      setRequestToDelete(null);
      await refreshUsersAndRequests(); // refresh list after deletion
    } catch (e) {
      console.error('Error deleting account request:', e);
    } finally {
      setDeletingRequest(false);
    }
  };


  const handleUserAdded = async () => {
    await refreshUsersAndRequests();
    setShowAddModal(false);
    setRequestToCreate(null); // NEW: clear after close
  };

  const handleUserUpdated = async () => {
    await refreshUsersAndRequests();
    setShowEditModal(false);
  };

  const handleUserDeleted = async () => {
    setLoadingDelete(true);
    try {
      const response = await fetch(
        'https://us-central1-fleet-sales-system.cloudfunctions.net/deleteUser',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: userToDelete.id }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        await deleteData('users', userToDelete.id);
        await refreshUsersAndRequests();
        setShowDeleteModal(false);
      } else {
        console.error('Error:', result.error);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortColumn === column && sortDirection === 'asc') direction = 'desc';
    setSortColumn(column);
    setSortDirection(direction);
  };

  useEffect(() => {
    const sortData = (data, column, direction) => {
      if (!column) return data;
      const sorted = [...data].sort((a, b) => {
        if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
        if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    };
    setFilteredUsers(prev => sortData(prev, sortColumn, sortDirection));
  }, [sortColumn, sortDirection]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredUsers.slice(indexOfFirstRow, indexOfLastRow);

  const renderPaginationItems = () => {
    const pageNumbers = [];
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const maxPagesToShow = 5;
    let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1);
    let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);
    if (endPage - startPage + 1 < maxPagesToShow)
      startPage = Math.max(endPage - maxPagesToShow + 1, 1);

    pageNumbers.push(
      <Pagination.First key="first" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
    );
    pageNumbers.push(
      <Pagination.Prev key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
    );
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }
    pageNumbers.push(
      <Pagination.Next key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
    );
    pageNumbers.push(
      <Pagination.Last key="last" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
    );
    return pageNumbers;
  };

  return (
    <>
      {/* ===== PENDING ACCOUNT REQUESTS ===== */}
      {pendingRequests.length > 0 && (
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <h5 className="mb-0">Account requests</h5>
            <Badge bg="warning" text="dark">{pendingRequests.length}</Badge>
          </div>
          <Alert variant="secondary" className="py-2">
            Click <strong>Create user</strong> to prefill the form. You’ll choose the actual Customer(s) below.
          </Alert>
          <Table size="sm" hover responsive>
            <thead>
              <tr>
                <th style={{ width: 240 }}>Requested</th>
                <th>Name</th>
                <th>Email</th>
                <th>Requested Company</th>
                <th style={{ width: 240 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {pendingRequests
                .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                .map(req => (
                  <tr key={req.id}>
                    <td>{fmt(req.createdAt)}</td>
                    <td>{req.fullName || req.name || '-'}</td>
                    <td>{req.email}</td>
                    <td>{req.company || '-'}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => { setRequestToCreate(req); setShowAddModal(true); }}
                        >
                          Create user
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => {
                            setRequestToDelete(req);
                            setShowRequestDeleteModal(true);
                          }}
                          title="Delete request"
                        >
                          <Trash size={16} className="me-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* ===== USERS HEADER + SEARCH ===== */}
      <InputGroup className="mb-2">
        <InputGroup.Text id="search-addon"><Search /></InputGroup.Text>
        <FormControl
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </InputGroup>

      <Table hover className="mt-3">
        <thead>
          <tr>
            <th>
              <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add User</Tooltip>}>
                <Button
                  onClick={() => { setRequestToCreate(null); setShowAddModal(true); }} // NEW: clear request when adding blank
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  className="icon-hover me-3"
                  variant="link"
                >
                  {isHovered ? <PlusCircleFill size={20} /> : <PlusCircle size={20} />}
                </Button>
              </OverlayTrigger>
            </th>
            <th onClick={() => handleSort('name')}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Name</span><ArrowDownUp />
              </div>
            </th>
            <th onClick={() => handleSort('email')}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Email</span><ArrowDownUp />
              </div>
            </th>
            <th onClick={() => handleSort('customer')}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Customer</span><ArrowDownUp />
              </div>
            </th>
            <th onClick={() => handleSort('upfitter')}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Upfitter</span><ArrowDownUp />
              </div>
            </th>
            <th onClick={() => handleSort('role')}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Role</span><ArrowDownUp />
              </div>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.map(user => (
            <tr key={user.id}>
              <td></td>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{getCustomerNames(user.customers)}</td>
              <td>{getUpfitterName(user.upfitter)}</td>
              <td>{user.role}</td>
              <td>
                <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Detail</Tooltip>}>
                  <Button
                    onClick={() => { setUserToShow(user); setShowDetailModal(true); }}
                    variant="white"
                    style={{ padding: '2px 4px' }}
                  >
                    <InfoCircle color="blue" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Edit</Tooltip>}>
                  <Button
                    onClick={() => { setUserToEdit(user); setShowEditModal(true); }}
                    variant="white"
                    style={{ padding: '2px 4px' }}
                  >
                    <Wrench color="black" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Delete</Tooltip>}>
                  <Button
                    onClick={() => { setUserToDelete(user); setShowDeleteModal(true); }}
                    variant="white"
                    style={{ padding: '2px 4px' }}
                  >
                    <Trash color="red" />
                  </Button>
                </OverlayTrigger>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center my-3 mb-5" style={{ flexWrap: 'wrap' }}>
        <div>Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, totalRows)} of {totalRows} rows</div>
        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ width: 'auto' }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </div>
        <Pagination className="mb-0">{renderPaginationItems()}</Pagination>
      </div>

      {/* Delete Account Request Modal */}
      <Modal
        show={showRequestDeleteModal}
        onHide={() => { if (!deletingRequest) setShowRequestDeleteModal(false); }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Account Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requestToDelete ? (
            <>
              Are you sure you want to delete this request for
              {' '}<strong>{requestToDelete.email}</strong>?
              <div className="mt-2 text-muted small">
                This will not create a user and the request will be permanently removed.
              </div>
            </>
          ) : 'Are you sure you want to delete this request?'}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRequestDeleteModal(false)}
            disabled={deletingRequest}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteRequest}
            disabled={deletingRequest}
          >
            {deletingRequest ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                {' '}Deleting…
              </>
            ) : 'Delete request'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add / Edit / Detail / Delete modals */}
      <Modal show={showAddModal} onHide={() => { setShowAddModal(false); setRequestToCreate(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>{requestToCreate ? 'Create User from Request' : 'Add User'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UserForm
            onClose={handleUserAdded}
            userRole={getUserRole()}
            request={requestToCreate} // NEW
          />
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UserForm user={userToEdit} onClose={handleUserUpdated} userRole={getUserRole()} />
        </Modal.Body>
      </Modal>

      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userToShow && (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
              <div><strong>Name:</strong> {userToShow.name}</div>
              <div><strong>Email:</strong> {userToShow.email}</div>
              <div><strong>Customer:</strong> {getCustomerName(userToShow.customer)}</div>
              <div><strong>Upfitter:</strong> {getUpfitterName(userToShow.upfitter)}</div>
              <div><strong>Role:</strong> {userToShow.role}</div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton><Modal.Title>Confirm Delete</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to delete this user?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loadingDelete}>Cancel</Button>
          <Button variant="danger" onClick={handleUserDeleted} disabled={loadingDelete}>
            {loadingDelete ? (<><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Deleting...</>) : ('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UserList;
